/**
 * Booking URL helper — generates links to booking.aheadhealth.com
 * with locale and optional product pre-selection.
 */

import { applyPersonHash } from "@/lib/person-hash-link"

const BOOKING_BASE = "https://booking.aheadhealth.com"

/** Product IDs from the booking system */
export const PRODUCT_IDS = {
  core: "3b7895a5-b892-44f9-9175-c2f7fd8e7962",
  advanced: "5147c324-1776-4e0a-9cc4-cff5bbcb18c0",
  pro: "64c6c5cb-95c4-4310-b8b5-ff68abfe7b38",
  bloodTest: "d9570d5a-8400-49c9-913f-44c1b2098dc8",
  // Women's hormone & PCOS add-on (ride alongside a base test in `products=`).
  // Source: ahead-booking-new TimeSlotsDisplay.tsx WOMEN_HORMONE_PRODUCT_ID.
  hormoneWomen: "e57e0619-d6bf-4f27-9e42-cecc4ebcaabb",
} as const

/**
 * Booking-flow steps the app accepts via the `step` query param
 * (src/utils/urlUtils.ts → VALID_STEPS in ahead-booking-new).
 */
export type BookingStep =
  | "product"
  | "addons"
  | "clinic"
  | "date"
  | "details"
  | "confirmation"

/**
 * The booking app's product-category tabs, read from the `packageType` query
 * param (ahead-booking-new: `PackageTypeToggle` + `bookingFlowStore` →
 * `selectedPackageType`).
 */
export type BookingPackageType = "mri" | "blood-only" | "membership"

/**
 * Maps a marketing-site product category to the booking app's `packageType`
 * tab. Pass this on every booking link so the destination tab is deterministic:
 * the booking app PERSISTS `selectedPackageType` in Zustand and otherwise falls
 * back to the last-used (or default `mri`) tab — so without an explicit
 * `packageType`, a membership or blood CTA can dump the user on the MRI tab.
 */
export const BOOKING_PACKAGE_TYPE: Record<
  "blood" | "mri" | "membership",
  BookingPackageType
> = {
  blood: "blood-only",
  mri: "mri",
  membership: "membership",
}

/**
 * Generate a booking URL with locale, optional product(s), and entry step.
 *
 * Pass a single ID for the base-tier-only case, or an array (base tier
 * first, then any add-on UUIDs) to ship a pre-filled "cart".
 *
 * Format: ONE `products` query value with comma-separated UUIDs —
 * `?products=a,b,c`. The booking app (`ahead-booking-new`,
 * `stores/bookingFlowStore.ts` → `initializeFromUrl`) reads this single
 * param, then splits the list by `product_type`: packages become the
 * selected base, everything else becomes `selectedAddOns`. So a cart's
 * base AND add-ons all ride in `products=` together — verified live
 * against production (Advanced + omega + calcium + DEXA all preselected).
 *
 * Do NOT split add-ons into a separate `addOns=` param: the `products=`
 * path ignores it, so the add-ons silently fail to preselect. (A
 * `product=`/`addOns=` shape exists in the app's test-mode path, but the
 * production `products=` path is the supported one and also keeps the
 * email-capture step intact.) And do NOT use repeated `?products=a&products=b`
 * — `URLSearchParams.get` returns only the first, dropping the rest.
 *
 * @param locale - "en" | "de" | "fr"
 * @param productIds - single base UUID, or `[base, ...addOnIds]`
 * @param step - optional booking step to deep-link into (e.g. "addons")
 * @param packageType - optional booking-app tab to force (`mri` / `blood-only`
 *   / `membership`). See {@link BOOKING_PACKAGE_TYPE} for why this matters.
 */
export function bookingUrl(
  locale: string,
  productIds?: string | readonly string[],
  step?: BookingStep,
  packageType?: BookingPackageType
): string {
  const params = new URLSearchParams()
  if (productIds) {
    const list = Array.isArray(productIds) ? productIds : [productIds]
    const clean = list.filter((id): id is string => !!id)
    if (clean.length > 0) params.set("products", clean.join(","))
  }
  if (packageType) params.set("packageType", packageType)
  if (step) params.set("step", step)
  params.set("locale", locale)
  applyPersonHash(params)
  return `${BOOKING_BASE}/?${params.toString()}`
}

/**
 * The markets whose booking links carry an explicit `country=` param, and the
 * booking-app tab each one opens by default.
 *
 * The booking app's own default market is `ch` (`DEFAULT_MARKET` in
 * ahead-booking-new `src/config/market.ts`), so a link that omits `country=`
 * hands a German or Dutch visitor Swiss clinics and CHF prices. Both markets
 * also enter on the product-selection step, because their check-up rollout
 * opens blood booking only.
 *
 * `defaultPackageType` pins the tab when the call site doesn't name one: the
 * booking app persists `selectedPackageType`, so a Dutch blood CTA would
 * otherwise reuse whatever tab that browser last saw (default `mri`). NL books
 * blood only — MRI and membership stay coming-soon there — so `blood-only` is
 * the only tab a Dutch link should ever open. DE keeps no default: its MRI and
 * membership CTAs call `bookingUrl` directly and pass their own tab.
 *
 * AT joins this map on the day its booking opens.
 */
const MARKET_BOOKING_ENTRY: Record<
  string,
  { defaultPackageType?: BookingPackageType }
> = {
  de: {},
  nl: { defaultPackageType: BOOKING_PACKAGE_TYPE.blood },
}

/**
 * MRI `/go` landing pages — full-body MRI LPs whose booking CTAs must open the
 * booking app's MRI tab, not the membership/blood flow.
 *
 * Matches the public pathname after stripping `/<country>/<locale>`: exactly
 * `/go/checkup` or `/go/insurancemri` (each including its `/v/<code>`
 * experiment variants), or anything starting with `/go/fullbodymri` (the base
 * LP plus the city variants `/go/fullbodymri-geneva`, `-basel`, `-zug`,
 * `-zurich`, `-st-gallen`). AHD-7129.
 */
export function isMriGoPath(pathname: string): boolean {
  const bare = `/${pathname.split("/").filter(Boolean).slice(2).join("/")}`
  return (
    bare === "/go/checkup" ||
    bare.startsWith("/go/checkup/v/") ||
    bare === "/go/insurancemri" ||
    bare.startsWith("/go/insurancemri/v/") ||
    bare.startsWith("/go/fullbodymri")
  )
}

export function bookingUrlForCountry(
  country: string,
  locale: string,
  productIds?: string | readonly string[],
  step?: BookingStep,
  packageType?: BookingPackageType
): string {
  // country-gate-ignore: per-market booking entry during the checkup rollout, not access control
  const entry = MARKET_BOOKING_ENTRY[country]
  if (!entry) return bookingUrl(locale, productIds, step, packageType)
  const params = new URLSearchParams()
  if (productIds) {
    const list = Array.isArray(productIds) ? productIds : [productIds]
    const clean = list.filter((id): id is string => !!id)
    if (clean.length > 0) params.set("products", clean.join(","))
  }
  const tab = packageType ?? entry.defaultPackageType
  if (tab) params.set("packageType", tab)
  params.set("step", step ?? "product")
  params.set("country", country)
  params.set("locale", locale)
  applyPersonHash(params)
  return `${BOOKING_BASE}/?${params.toString()}`
}

/**
 * Booking URL for the Ahead Membership sign-up flow.
 *
 * Since 2026-07 this delegates to {@link membershipBookingAppUrl} — the
 * standalone membership.aheadhealth.com app is deprecated for acquisition;
 * ALL membership sign-ups (both arms of the membership-first experiment
 * included) route through the unified booking-app cadence flow so the
 * experiment isolates upper-funnel framing, not booking-flow strength, and
 * every sign-up fires the same `booking_step` tracking. Kept as a named
 * export so the many "membership CTA" call sites read naturally.
 */
export function membershipBookingUrl(locale: string): string {
  return membershipBookingAppUrl(locale)
}

/**
 * Deep link into the membership plan / cadence selection step (1 / 2 / 4
 * tests per year). The booking app's `step=cadence` screen IS the plan
 * chooser, so this too delegates to {@link membershipBookingAppUrl} (the
 * old membership.aheadhealth.com/join/membership-plan deep link is
 * deprecated along with the app — see membershipBookingUrl above).
 */
export function membershipPlanUrl(locale: string): string {
  return membershipBookingAppUrl(locale)
}

/**
 * Membership sign-up via the **booking app** — the canonical membership
 * sign-up URL. booking.aheadhealth.com sells Ahead membership directly; since
 * 2026-07 every membership CTA routes here (one onboarding flow), originally
 * scoped to the membership-first experiment arm.
 *
 * Lands on the **blood tab**, not the membership tab, on purpose (AHD-9123).
 * `packageType=membership` renders a membership-only card whose "Become a
 * member" button leaves for the deprecated `membership.aheadhealth.com`
 * (`ProductStep.tsx` only wires the in-app cadence entry on the blood-only
 * side-by-side layout), so that tab is a dead end: of 56 membership-first users
 * who reached it during experiment 86450, 43 went no further. `blood-only`
 * instead renders the "Ahead membership" + "Single blood test" comparison, and
 * its membership CTA enters the real in-app cadence step. Flip back once the
 * booking app wires the membership tab to `handleStartMembership`.
 *
 * `step=cadence` stays in the href even though the booking app currently
 * bounces a cold cadence link to the product step: it's the unique signal the
 * PostHog membership-signup metric filters on (and the single-test guardrail
 * excludes on), and it lands directly on the plan chooser once the booking
 * app's cadence step learns to seed its own baseline package.
 */
export function membershipBookingAppUrl(locale: string): string {
  const params = new URLSearchParams()
  // Force the tab — the booking app otherwise restores its persisted (or
  // default `mri`) tab, dropping "Start from CHF 299/year" on the MRI scan.
  params.set("packageType", BOOKING_PACKAGE_TYPE.blood)
  params.set("locale", locale)
  params.set("step", "cadence")
  // Carry the email→app `?ph` person hash across the hop (parity with every
  // other outbound booking link) so email-attributed sessions stay stitched.
  applyPersonHash(params)
  return `${BOOKING_BASE}/?${params.toString()}`
}
